import React, { useEffect, useState } from 'react';
import { EntityType, SupplierLocationMappingListConfiguration as GeoGroupsConfiguration } from '../../integrated-client/models';
import {
  ActionOptions,
  BindProperties,
  Checkboxes,
  ConfigurationMethods,
  DEFAULT_PAGE_SIZE,
  DEFAULT_TOTAL_ITEMS,
  ItemTypes,
  MIME_TYPES,
  SectionKeys,
} from '../../utils/Constants';
import RestClient from '../../integration/RestClient';
import FiltersLayoutV2 from './FiltersLayoutV2';
import TableLayoutV2 from './TableLayoutV2';
import { ColumnProps } from 'antd/lib/table';
import { CATEGORIES } from '../../utils/PermissionsChecker';
import { FiltersData as SearchManagerV2FiltersData } from '../../store/state/SearchManagerV2/FiltersData';
import { IItem, IRow } from '../EditorLayout/EditorLayout';
import ActionModalWindowWrapper from '../ModalWindows/ModalWindowWrappers/ActionModalWindowWrapper';
import { downloadFile, getObjWithoutNullValues, usePrevious } from '../../utils/MiscUtils';
import HistoryModal from '../ModalWindows/ModalWindowWrappers/HistoryModal';
import { NotificationActions } from '../../store/actions/notificationActions';
import { NotificationType } from '../../views/NotificationsManager/Notification/Notification';
import OsiBulkUpdateModalWrapper from '../ModalWindows/ModalWindowWrappers/OsiBulkUpdateModalWrapper';

// to be replaced and to be changed
export type FiltersData = SearchManagerV2FiltersData;
export type Configuration = null;
export type TableData = null;

export interface IActionOptions {
  whenFullControl: Array<ActionOptions>;
  whenReadOnly: Array<ActionOptions>;
}
export interface ISelectedRowsObject {
  selected?: boolean;
  selectedRowKeys: any[];
  selectedRows: any[];
}

export interface FiltersTableLayoutsWrapperV2Props {
  layoutFilters:
    | {
        title: string;
        rows: Array<IRow> | [];
        filtersData: FiltersData;
        configurationMethod: ConfigurationMethods;
        actionClassFilters: any;
        actionClassEditor: any;
        transformConfiguration?: () => any;
      }
    | any;
  layoutTable:
    | {
        columns: Array<ColumnProps<any>>;
        tableData: TableData;
        actionClassTable: any;
        historyType: EntityType;
        addButton?: boolean;
        addButtonName?: string;
        actionOptions: IActionOptions;
        draggable: boolean;
        resizable: boolean;
        showExportButton?: boolean;
      }
    | any; // temporary
  sectionKey: SectionKeys;
  path: string;
  menuListProperty: keyof Configuration;
  actionClassEditor: any;
  bindProperty: BindProperties;
  category?: CATEGORIES;
  history: any;
  entityType: EntityType;
}

const FiltersTableLayoutsWrapperV2: React.FC<FiltersTableLayoutsWrapperV2Props> = (props) => {
  const { layoutFilters, layoutTable, history, sectionKey, path, category, actionClassEditor, entityType } = props;
  const { title, filtersData, configurationMethod, actionClassFilters, rows, transformConfiguration } = layoutFilters || {};
  const { columns, tableData, actionClassTable, historyType, addButton, addButtonName, actionOptions, draggable = true, resizable = true, showExportButton } = layoutTable || {};

  const [configuration, setConfiguration] = useState<any | null>(null);

  //Clear filters states
  const [filtersAreCleared, setFiltersAreCleared] = useState<boolean>(false);
  const [clearValues, setClearValues] = useState<Array<keyof FiltersData> | null>(null);

  //State for filter's collapse
  const [activeKeys, setActiveKeys] = useState<Array<string> | string>(['1']);
  const [selectedRowsObject, setSelectedRowsObject] = useState<ISelectedRowsObject>({ selectedRowKeys: [], selectedRows: [] });
  const [currentPage, setCurrentPage] = useState<number>(1);

  //Modal window states
  const [showModalWindow, setShowModalWindow] = useState<boolean>(false);
  const [modalWindowType, setModalWindowType] = useState<ActionOptions | null>(null);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState<boolean>(false);

  //Table states for Select All and Select Current Page
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [selectCurrentPage, setSelectCurrentPage] = useState<boolean>(false);
  const [tableRecords, setTableRecords] = useState([]);
  const [tableTotalItems, setTableTotalItems] = useState();
  const [totalRecords, setTotalRecords] = useState(tableRecords);
  const [filtersBrandAndMarketSelected, setFiltersBrandAndMarketSelected] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [lastSearchedQuery, setLastSearchedQuery] = useState<any>(null);

  const previousTableTotalItems = usePrevious(tableTotalItems);

  // Filter's methods block
  const updateFiltersData = (value: any) => actionClassFilters.updateFiltersData(value);
  const resetClearValues = () => setClearValues(null);

  const handleSearchButtonClick = () => {
    resetPaginationParams().then(() =>
      getData().then((result: any) => {
        setSelectCurrentPage(false);
        setAllSelected(false);
        handleRowSelect([], []);
      })
    );

    const brandAndMarketSelected = filtersData.brandAndMarket !== undefined && filtersData.brandAndMarket !== null;
    const carrierSelected = filtersData.carrier !== undefined && filtersData.carrier !== null;

    if (brandAndMarketSelected || carrierSelected) {
      setFiltersBrandAndMarketSelected(true);
    } else {
      setFiltersBrandAndMarketSelected(false);
    }
  };

  const resetPaginationParams = async () => {
    return await updateFiltersData({
      offset: 0,
      currentPage: 1,
      total: DEFAULT_TOTAL_ITEMS,
    });
  };

  const prepareSearchObj = (filtersData: FiltersData) => {
    return {
      ...filtersData,
    };
  };

  const getData = async () => {
    const defaultFiltersData = {
      offset: 0,
      limit: 20,
      total: 0,
      currentPage: 1,
    };
    const submitData = prepareSearchObj(filtersData || defaultFiltersData);
    setLastSearchedQuery(submitData);
    const response = await actionClassTable.fetchData(submitData).then((res: any) => {
      const items = res.items?.map((record: any) => {
        if (record.state !== undefined) return record;
        return {
          ...record,
          state: [
            ...(record.disabled ? ['Disabled'] : []),
            ...(record.deleted ? ['Deleted'] : []),
          ],
        };
      });
      setTableRecords(items);
      setTableTotalItems(res.totalItems);
    });
    return response;
  };

  //Get data methods
  useEffect(() => {
    if (configurationMethod && (RestClient as any)[sectionKey]?.list?.[configurationMethod]) {
      (RestClient as any)[sectionKey].list[configurationMethod]({})
        .then((result: any) => {
          transformConfiguration ? setConfiguration(transformConfiguration(result)) : setConfiguration(result);
        })
        .catch(() => {});
    }
    layoutTable && getData();
  }, []);

  //Get data methods using datasource
  useEffect(() => {
    (rows as Array<IRow>).map((row) => {
      row.items.map((item) => {
        if (item.dataSource?.action && item.dataSource?.resolveParams) {
          const self = item.dataSource;
          item.dataSource.action.apply(self, item.dataSource?.resolveParams());
        } else if (item.dataSource?.action) {
          item.dataSource.action();
        }
      });
    });
  }, []);

  //fetching total records fo the table to select all records on select all checkbox checked event
  useEffect(() => {
    if (previousTableTotalItems !== tableTotalItems && filtersData) {
      const submitData = { ...filtersData, limit: tableTotalItems };
      actionClassTable.fetchData(prepareSearchObj(submitData)).then((result: any) => {
        setTotalRecords(result?.items);
      });
    }
  }, [tableTotalItems]);

  //set/unset current page selection checkbox
  const prevRowsData = usePrevious(selectedRowsObject) as any;

  useEffect(() => {
    if (prevRowsData !== selectedRowsObject && prevRowsData?.selectedRowKeys.length > selectedRowsObject.selectedRowKeys.length) {
      setSelectCurrentPage(false);
      setAllSelected(false);
    } else if (selectedRowsObject.selectedRowKeys.length === tableTotalItems && currentPage !== 1) {
      setAllSelected(true);
    } else if (DEFAULT_PAGE_SIZE <= selectedRowsObject.selectedRowKeys.length) {
      setSelectCurrentPage(true);
    } else if (
      tableRecords.length === selectedRowsObject.selectedRowKeys.length &&
      selectedRowsObject.selectedRowKeys.length !== 0 &&
      tableRecords.length !== 0
    ) {
      setSelectCurrentPage(true);
    }
  }, [selectedRowsObject.selectedRowKeys]);
  //End of set/unset current page selection checkbox

  //Clear filters methods
  const handleClearButtonClick = () => {
    actionClassFilters.resetFiltersData();
    let newClearValues: any = [];
    if (rows) {
      rows.forEach((row: IRow) => {
        const autoSuggestIds = row.items.filter((item: IItem) => item?.type === ItemTypes.AutoSuggest).map((item: IItem) => item?.id);
        newClearValues = [...newClearValues, ...autoSuggestIds];
      });
      setClearValues(newClearValues);
    }
    setFiltersAreCleared(true);
    setFiltersBrandAndMarketSelected(false);
  };

  //Export methods
  const handleExportButtonClick = () => {
    if (!lastSearchedQuery && !filtersData) return;
    const baseQuery = lastSearchedQuery || prepareSearchObj(filtersData);
    const { pageNumber, pageSize, offset, limit, ...exportRequestObj } = getObjWithoutNullValues(baseQuery);
    const { selectedRows, selectedRowKeys } = selectedRowsObject;
    // When specific rows are selected (current page or individual records) but NOT all pages,
    // pass their IDs so only those records are exported.
    // When allSelected, no IDs are needed — the full filter query already covers all pages.
    if (selectedRowKeys.length > 0 && !allSelected) {
      exportRequestObj.ids = selectedRowKeys;
    }
    RestClient.markupV2.list
      .apiMarkupV2MarkupV2ListExportPost({ markupV2Query: exportRequestObj })
      .then((res: any) => downloadFile(res, 'markups.csv', MIME_TYPES.CSV))
      .catch((e: any) => {
        e.json().then((error: any) => {
          if (error.status === 404) {
            return NotificationActions.addNotification({
              type: NotificationType.WARNING,
              message: `There are no markups to export.`,
            });
          }
        });
      });
  };

  useEffect(() => {
    if (filtersAreCleared) {
      getData();
      setFiltersAreCleared(false);
    }
  }, [filtersData]);

  //Sort methods
  const handleSortClick = (sortValue?: string) => {
    if (!sortValue || !filtersData) return;
    const { orderValue, orderByDescending } = filtersData;
    const newOrderByDescending = sortValue !== orderValue || orderByDescending === undefined ? false : orderByDescending === true ? undefined : true;
    const newOrderValue = newOrderByDescending !== undefined ? sortValue : undefined;
    handlePageChange(1);
    updateFiltersData({ orderValue: newOrderValue, orderByDescending: newOrderByDescending });
  };

  const prevOrderValue = usePrevious(filtersData?.orderValue);

  useEffect(() => {
    // prevOffset !== filtersData.offset - exclude double request if user clicked on sorted header on page number > 1
    if ((prevOrderValue || filtersData?.orderValue) && prevOffset === filtersData?.offset) getData();
  }, [filtersData?.orderValue, filtersData?.orderByDescending]);

  //Table buttons handlers
  const handleActionOptionClick = async (actionOption: ActionOptions) => {
    const { selectedRows } = selectedRowsObject;
    const selectedRowsIds = selectedRows.map((row) => {
      return row.id;
    });
    switch (actionOption) {
      case ActionOptions.Edit:
        actionClassEditor?.setSelectedRowKeys && actionClassEditor?.setSelectedRowKeys(selectedRows);
        history.push({ pathname: `/${path}/${selectedRows[0]?.id}`, selectedRowsIds: selectedRowsIds });
        break;
      case ActionOptions.View:
        actionClassEditor?.setSelectedRowKeys && actionClassEditor?.setSelectedRowKeys(selectedRows);
        history.push({
          pathname: `/${path}/${selectedRows[0]?.id}`,
          viewDisabled: selectedRows[0]?.allowEdit,
          actionOption: ActionOptions.View,
          selectedRowsIds: selectedRowsIds,
        });
        break;
      case ActionOptions.Delete:
      case ActionOptions.Copy:
        setShowModalWindow(true);
        setModalWindowType(actionOption);
        break;
      case ActionOptions.History:
        setShowHistoryModal(true);
        break;
      case ActionOptions.Enable:
        const enableKeys = selectedRowsObject.selectedRowKeys.map((key) => {
          const enabledKey = { id: key, disabled: false };
          return enabledKey;
        });
        const enableItemPromise = await actionClassTable.disableEnableKeys(enableKeys);
        Promise.all(enableItemPromise).then(() => {
          setSelectedRowsObject({ selectedRows: [], selectedRowKeys: [] });
          getData();
        });
        break;
      case ActionOptions.Disable:
        const disableKeys = selectedRowsObject.selectedRowKeys.map((key) => {
          const disabledKey = { id: key, disabled: true };
          return disabledKey;
        });
        const disableItemPromise = await actionClassTable.disableEnableKeys(disableKeys);
        Promise.all(disableItemPromise).then(() => {
          setSelectedRowsObject({ selectedRows: [], selectedRowKeys: [] });
          getData();
        });
        break;
      case ActionOptions.BulkUpdate:
        setShowBulkUpdateModal(true);
        break;
    }
  };

  const checkboxActions = (id: string) => {
    if (id === Checkboxes.SelectCurrentPage && tableTotalItems! > 0) {
      if (selectCurrentPage) {
        setSelectCurrentPage(false);
        handleRowSelect([], []);
      } else {
        setSelectCurrentPage(true);
        handleRowSelect(
          tableRecords.map((item: any) => {
            return item.id;
          }),
          tableRecords
        );
      }
    } else if (id === Checkboxes.SelectAllPages && tableTotalItems! > DEFAULT_PAGE_SIZE) {
      if (allSelected) {
        setAllSelected(false);
        handleRowSelect([], []);
      } else {
        setAllSelected(true);
        setSelectCurrentPage(true);
        handleRowSelect(
          totalRecords.map((item: any) => {
            return item.id;
          }),
          totalRecords
        );
      }
    }
  };

  const checkcheckbox = (id: string) => {
    if (id === Checkboxes.SelectCurrentPage) {
      return selectCurrentPage;
    } else if (id === Checkboxes.SelectAllPages && tableTotalItems! > DEFAULT_PAGE_SIZE) {
      return allSelected;
    }
  };

  const handleAddRuleButtonClick = () => {
    history?.push(`/${path}`);
  };

  const handleRowSelect = (selectedRowKeys: any[], selectedRows: any[]) => {
    setSelectedRowsObject({
      selectedRows,
      selectedRowKeys,
    });
  };

  //Modal window buttons handlers
  const handleCancelButtonClick = () => {
    setShowModalWindow(false);
    setShowHistoryModal(false);
    setModalWindowType(null);
  };

  const handleOkButtonClick = async () => {
    const { selectedRows } = selectedRowsObject;
    const selectedRowsIds = selectedRows.map((row) => {
      return row.id;
    });
    switch (modalWindowType) {
      case ActionOptions.Delete:
        const deleteSelectedKeys = selectedRowsObject.selectedRowKeys.map((key) => {
          const deletedKey = { id: key, deleted: true };
          return deletedKey;
        });
        const deleteItemPromises = await actionClassTable.deleteKeys(deleteSelectedKeys);
        handleCancelButtonClick();
        Promise.all(deleteItemPromises).then(() => {
          setSelectedRowsObject({ selectedRows: [], selectedRowKeys: [] });
          getData();
        });
        break;
      case ActionOptions.Copy:
                actionClassEditor?.setSelectedRowKeys && actionClassEditor?.setSelectedRowKeys(selectedRows);
                history.push(`/${path}`, { ifCopy: true, copyId: selectedRowsIds[0], copyIds: selectedRowsIds });
        break;
    }
  };

  //Pagination methods
  const previousPageNumber = usePrevious(currentPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    if (previousPageNumber !== pageNumber && selectCurrentPage && !allSelected) setSelectCurrentPage(false);
    if (!selectCurrentPage && !allSelected) handleRowSelect([], []);
    updateFiltersData({ offset: (pageNumber - 1) * DEFAULT_PAGE_SIZE });
  };

  const prevOffset = usePrevious(filtersData?.offset);

  useEffect(() => {
    if (prevOffset !== undefined) getData();
  }, [filtersData?.offset]);

  useEffect(() => {
    if (!selectCurrentPage && previousPageNumber !== currentPage) handleRowSelect([], []);
  }, [currentPage]);

  return (
    <>
      {layoutFilters && (
        <FiltersLayoutV2
          handleFiltersDataUpdate={updateFiltersData}
          filtersData={filtersData}
          configuration={configuration}
          clearValues={clearValues}
          activeKeys={activeKeys}
          setActiveKeys={setActiveKeys}
          setClearValues={setClearValues}
          resetClearValues={resetClearValues}
          rows={rows}
          title={title}
          handleClearButtonClick={handleClearButtonClick}
          handleSearchButtonClick={handleSearchButtonClick}
          handleExportButtonClick={handleExportButtonClick}
        />
      )}
      {layoutTable && (
        <TableLayoutV2
          id={sectionKey}
          history={history}
          records={tableRecords}
          actionOptions={actionOptions}
          totalItems={tableTotalItems}
          currentPage={currentPage}
          columns={columns}
          category={category}
          path={path}
          addButtonName={addButtonName}
          handlePageChange={handlePageChange}
          selectedRowsObject={selectedRowsObject}
          handleRowSelect={handleRowSelect}
          handleActionOptionClick={handleActionOptionClick}
          checkboxActions={checkboxActions}
          checkcheckbox={checkcheckbox}
          handleAddRuleButtonClick={handleAddRuleButtonClick}
          handleSortClick={handleSortClick}
          filtersBrandAndMarketSelected={filtersBrandAndMarketSelected}
          onExport={showExportButton ? handleExportButtonClick : undefined}
        />
      )}
      <ActionModalWindowWrapper
        showModalWindow={showModalWindow}
        type={modalWindowType}
        selectedRows={selectedRowsObject.selectedRows}
        handleCancelButtonClick={handleCancelButtonClick}
        handleOkButtonClick={handleOkButtonClick}
        path={path}
        history={history}
      />
      {showHistoryModal && (
        <HistoryModal
          showHistoryModal={showHistoryModal}
          setShowHistoryModal={setShowHistoryModal}
          actionType={ActionOptions.History}
          type={entityType}
          id={selectedRowsObject.selectedRows[0]?.id as string}
          handleRowSelect={handleRowSelect}
        />
      )}
      {showBulkUpdateModal && (   // ← ADD THIS
        <OsiBulkUpdateModalWrapper
          showModalWindow={showBulkUpdateModal}
          setShowModalWindow={setShowBulkUpdateModal}
          selectedRowsObject={selectedRowsObject}
          handleRowSelect={handleRowSelect}
          getData={getData}
        />
      )}
    </>
  );
};

export default FiltersTableLayoutsWrapperV2;
